import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { qk } from '@/lib/query';
import * as api from '@/api/misc';
import type { CompanyInfo, GeneralSettings, InvoiceSettings } from '@/types';

export function useGeneralSettings() {
  return useQuery({
    queryKey: qk.settings,
    queryFn: ({ signal }) => api.fetchGeneralSettings(signal),
    staleTime: 5 * 60_000
  });
}

export function useInvoiceSettings() {
  return useQuery({
    queryKey: qk.invoiceSettings,
    queryFn: ({ signal }) => api.fetchInvoiceSettings(signal),
    staleTime: 5 * 60_000
  });
}

export function useCompanyInfo() {
  return useQuery({
    queryKey: qk.companyInfo,
    queryFn: ({ signal }) => api.fetchCompanyInfo(signal),
    staleTime: 5 * 60_000
  });
}

/**
 * Settings forms write optimistically: the toggle moves under the finger and
 * reverts if the save fails. Anything else makes a switch feel broken.
 */
function useSettingsMutation<T>(
  key: readonly unknown[],
  mutationFn: (input: Partial<T>) => Promise<unknown>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    async onMutate(input) {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<T>(key);
      if (previous) qc.setQueryData<T>(key, { ...previous, ...input });
      return { previous };
    },
    onError(_e, _v, ctx) {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
      toast.error('Could not save that setting.');
    },
    onSettled(_data, _error, input) {
      void qc.invalidateQueries({ queryKey: key });
      // Reminder schedules are derived from the invoice settings.
      void qc.invalidateQueries({ queryKey: qk.remindersRoot });
      // The setup guide's "your firm" step reads the company name; let it tick at once.
      void qc.invalidateQueries({ queryKey: qk.setup });
      // The billing mode also lives on the session (`team.billingType`), which
      // is what `useBillingNouns` reads. Refetch it so Client/Project flips
      // everywhere without a reload.
      if (input && 'teamBillingType' in input) {
        void qc.invalidateQueries({ queryKey: qk.session });
      }
    }
  });
}

export const useSaveGeneralSettings = () =>
  useSettingsMutation<GeneralSettings>(qk.settings, api.saveGeneralSettings);

export const useSaveInvoiceSettings = () =>
  useSettingsMutation<InvoiceSettings>(qk.invoiceSettings, api.saveInvoiceSettings);

export const useSaveCompanyInfo = () =>
  useSettingsMutation<CompanyInfo>(qk.companyInfo, api.saveCompanyInfo);

// ---------------------------------------------------------------- reminders

export function useUpcomingReminders(enabled = true) {
  return useQuery({
    queryKey: qk.upcomingReminders,
    queryFn: ({ signal }) => api.fetchUpcomingReminders(signal),
    enabled
  });
}

export function useInvoiceReminders(invoiceId: string) {
  return useQuery({
    queryKey: qk.invoiceReminders(invoiceId),
    queryFn: ({ signal }) => api.fetchInvoiceReminders(invoiceId, signal)
  });
}

export function useSetInvoiceRemindersPaused(invoiceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paused: boolean) => api.setInvoiceRemindersPaused(invoiceId, paused),
    onSuccess(data) {
      qc.setQueryData(qk.invoiceReminders(invoiceId), data);
      void qc.invalidateQueries({ queryKey: qk.upcomingReminders });
      toast.success(data.paused ? 'Reminders paused for this invoice' : 'Reminders resumed');
    },
    onError() {
      toast.error('Could not update reminders.');
    }
  });
}

export function useTimezone() {
  return useQuery({
    queryKey: ['timezone'],
    queryFn: () => api.fetchTimezone(),
    staleTime: 60 * 60_000
  });
}

export function useSaveTimezone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.saveTimezone,
    async onMutate(timezone) {
      await qc.cancelQueries({ queryKey: ['timezone'] });
      const previous = qc.getQueryData(['timezone']);
      qc.setQueryData(['timezone'], timezone);
      return { previous };
    },
    onError(_e, _v, ctx) {
      qc.setQueryData(['timezone'], ctx?.previous);
      toast.error('Could not update your timezone.');
    },
    onSuccess() {
      toast.success('Timezone updated');
    }
  });
}

// --------------------------------------------------------------------- team

export function useTeamMembers(teamId: string | undefined) {
  return useQuery({
    queryKey: [...qk.teamMembers, teamId],
    queryFn: ({ signal }) => api.fetchTeamMembers(teamId!, signal),
    enabled: Boolean(teamId)
  });
}

export function useTeamInvites(teamId: string | undefined) {
  return useQuery({
    queryKey: [...qk.teamInvites, teamId],
    queryFn: ({ signal }) => api.fetchTeamInvites(teamId!, signal),
    enabled: Boolean(teamId)
  });
}

export function useSubscription() {
  return useQuery({
    queryKey: qk.subscription,
    queryFn: ({ signal }) => api.fetchSubscription(signal),
    staleTime: 5 * 60_000
  });
}
